import { useTranslation } from 'react-i18next';
import type { Quotation } from '@tailoring-crm/shared';
import { formatCurrency } from '../../lib/format.js';
import {
  useAcceptQuotation,
  useDiscardQuotation,
  useQuotationDownload,
  useSendQuotation,
  useSendQuotationOnWhatsapp,
} from '../../lib/quotations/use-quotations.js';
import { useConfirm, useToast } from '@tailoring-crm/ui';

/**
 * The things a shop DOES to a quotation, in one place because two screens do them.
 *
 * The card on a customer and the quotations list are different views of the same documents, and
 * the actions must not differ between them: sending has to ask the same question and name the
 * same amount whichever screen it was pressed from, or the confirmation stops being something
 * anybody reads. Keeping the handlers here is what makes that true by construction rather than
 * by two authors remembering.
 *
 * Declining is deliberately NOT here. It asks for a reason, which means a dialog and therefore
 * state that belongs to the screen holding it — see `reject-quotation-dialog.tsx`, which both
 * screens mount.
 */
export interface QuotationActions {
  /** Draws the number and freezes the figures. Confirmed first, naming the amount. */
  send: (quotation: Quotation) => void;
  /** Fetches a fresh short-lived link and opens the PDF. */
  open: (quotation: Quotation) => void;
  /** Sends the file from the shop's own WhatsApp workspace. */
  whatsapp: (quotation: Quotation) => void;
  accept: (quotation: Quotation) => void;
  /** A draft only. The API refuses to remove anything that has been sent. */
  discard: (quotation: Quotation) => void;
  busy: boolean;
  downloading: boolean;
}

export function useQuotationActions(): QuotationActions {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const send = useSendQuotation();
  const accept = useAcceptQuotation();
  const discard = useDiscardQuotation();
  const download = useQuotationDownload();
  const whatsapp = useSendQuotationOnWhatsapp();

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  return {
    send: (quotation) => {
      void (async () => {
        const confirmed = await confirm({
          title: t('quotations.send.title', { name: quotation.leadName }),
          description: t('quotations.send.description', {
            amount: formatCurrency(quotation.preTaxTotalPaise),
          }),
          confirmLabel: t('quotations.send.confirm'),
        });
        if (!confirmed) return;
        try {
          const posted = await send.mutateAsync(quotation.id);
          toast({
            title: t('quotations.sent', { number: posted.number ?? '' }),
            variant: 'success',
          });
        } catch (error) {
          report(error);
        }
      })();
    },

    open: (quotation) => {
      void (async () => {
        try {
          const link = await download.mutateAsync({ quotationId: quotation.id });
          window.open(link.url, '_blank', 'noopener,noreferrer');
        } catch (error) {
          report(error);
        }
      })();
    },

    whatsapp: (quotation) => {
      void (async () => {
        try {
          await whatsapp.mutateAsync(quotation.id);
          toast({ title: t('quotations.sharedOnWhatsapp'), variant: 'success' });
        } catch (error) {
          report(error);
        }
      })();
    },

    accept: (quotation) => {
      void (async () => {
        const confirmed = await confirm({
          title: t('quotations.accept.title', { number: quotation.number ?? '' }),
          description: t('quotations.accept.description', { name: quotation.leadName }),
          confirmLabel: t('quotations.accept.confirm'),
        });
        if (!confirmed) return;
        try {
          await accept.mutateAsync(quotation.id);
          toast({ title: t('quotations.accepted'), variant: 'success' });
        } catch (error) {
          report(error);
        }
      })();
    },

    discard: (quotation) => {
      void (async () => {
        const confirmed = await confirm({
          title: t('quotations.discard.title'),
          description: t('quotations.discard.description'),
          confirmLabel: t('quotations.discard.confirm'),
          destructive: true,
        });
        if (!confirmed) return;
        try {
          await discard.mutateAsync(quotation.id);
          toast({ title: t('quotations.discarded'), variant: 'success' });
        } catch (error) {
          report(error);
        }
      })();
    },

    busy: send.isPending || accept.isPending || discard.isPending || whatsapp.isPending,
    downloading: download.isPending,
  };
}
